<template>
  <!-- Видео -->
  <div
    v-if="isVideo && videoSrc"
    class="video-wrapper media-card-layer-content w-full"
    :style="videoWrapperStyles"
  >
    <VideoCard
      :video-src="videoSrc"
      :autoplay="videoProps?.autoplay ?? true"
      :muted="videoProps?.muted ?? true"
      :loop="videoProps?.loop ?? true"
      :options="{
        borderRadius: videoBorderRadius,
        zoom: videoProps?.zoom ?? 100,
        positionX: videoProps?.positionX ?? 0,
        positionY: videoProps?.positionY ?? 0,
      }"
      :style="computedShadowStyle ? { filter: computedShadowStyle } : {}"
    />
  </div>

  <!-- Изображение -->
  <div
    v-else-if="isImage && imageSrc"
    class="image-container media-card-layer-content w-full"
    :style="imageContainerStyles"
  >
    <!-- Для изображения с маской используем img, для без маски - v-image-async -->
    <!-- Transform и object-fit применяются на уровне MediaTransformLayer -->
    <img
      v-if="hasMask && isMaskEnabled"
      :src="imageSrc"
      :alt="'Image'"
      :key="'img-' + maskShape"
      loading="lazy"
      class="w-full h-full"
      :style="containerStyles"
    />
    <v-image-async
      v-else
      :src="imageSrc"
      class="w-full h-full"
      :style="containerStyles"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from "vue";
import VideoCard from "../../VideoCard.vue";
import VImageAsync from "../../../vendor/VImageAsync/VImageAsync.vue";
import { useMediaContent } from "../composables/useMediaContent";
import { useMediaShadow } from "../composables/useMediaShadow";
import { useMediaMask } from "../composables/useMediaMask";
import type { ImageProps as ImagePropsForContent, VideoProps as VideoPropsForContent } from "../composables/useMediaContent";
import type { ShadowProps } from "../composables/useMediaShadow";
import type { MaskProps } from "../composables/useMediaMask";

interface Props {
  // Объект со всеми вычислениями из useMediaCard
  mediaCardState: ReturnType<(...args: any[]) => any>;
  mediaType?: "image" | "video";
  imageProps?: ImagePropsForContent;
  videoProps?: VideoPropsForContent;
  isVisible: boolean;
  shadowProps?: ShadowProps;
  maskProps?: MaskProps;
  objectFit?: "cover" | "contain";
}

const props = defineProps<Props>();

const { isVideo, isImage, videoSrc, imageSrc } = useMediaContent(
  computed(() => props.mediaType),
  computed(() => props.imageProps),
  computed(() => props.videoProps)
);

const { shadowStyle: computedShadowStyle } = useMediaShadow(computed(() => props.shadowProps));

const { maskShape, hasMask, isMaskEnabled } = useMediaMask(computed(() => props.maskProps));

const objectFit = computed(() => props.objectFit || "unknown");

// Используем готовые стили из composable
const containerStyles = computed(() => {
  if (!props.mediaCardState?.contentStyles) return {};
  return props.mediaCardState.contentStyles.value || {};
});

// Отримуємо borderRadius для передачі в VideoCard
const videoBorderRadius = computed(() => {
  const borderRadius = containerStyles.value.borderRadius;
  if (!borderRadius) return undefined;
  // Конвертуємо "10px" в число 10
  if (typeof borderRadius === 'string') {
    const match = borderRadius.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : undefined;
  }
  return typeof borderRadius === 'number' ? borderRadius : undefined;
});

const videoWrapperStyles = computed(() => {
  // Видаляємо borderRadius з контейнера, щоб він застосовувався тільки до відео через options
  const { borderRadius, ...stylesWithoutBorderRadius } = containerStyles.value;
  return {
    ...stylesWithoutBorderRadius,
    overflow: 'visible',
  };
});

const imageContainerStyles = computed(() => {
  // Видаляємо borderRadius з контейнера, щоб він застосовувався тільки до img елемента
  const { borderRadius, ...stylesWithoutBorderRadius } = containerStyles.value;
  return {
    ...stylesWithoutBorderRadius,
    overflow: 'visible',
  };
});

</script>

<style scoped>
.image-container {
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-wrapper {
  width: auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  position: relative;
}

.video-wrapper :deep(.v-video-container) {
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
}

.video-wrapper :deep(.regular-video-element) {
  width: 100% !important;
  height: 100% !important;
}

.video-wrapper.h-full :deep(.v-video-container) {
  height: 100% !important;
}

.video-wrapper.h-full :deep(.regular-video-element) {
  height: 100% !important;
}

:deep(.video-stream.html5-main-video) {
  width: 100% !important;
  height: auto !important;
  top: 0 !important;
}
</style>

